import * as React from 'react'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'
import { P, P16, GoodSansB1, colors } from '@components/shared'
import type { SubscriptionLengthType } from '../../../flowTypes'
import Radio from '@components/shared/Radio'
import { useContext } from 'react'
import ThemeContext from '@components/shared/ThemeContext'

type OwnPropsType = {
  formId: string,
  onClick: (event: any) => void,
  selected: boolean,
  text: string,
  arialLabel: string,
  planName: SubscriptionLengthType,
  name: string,
  badgeText?: string,
  billedText: string,
  promoText?: string,
  crossOutText?: boolean,
  price?: string,
  isAnnualOnly: boolean,
  subscriptionPromoText?: string
}

export function SubscriptionPlanOption (props: OwnPropsType): React.ReactNode {
  const theme = useContext(ThemeContext)

  const {
    name,
    onClick,
    selected,
    text,
    badgeText,
    planName,
    formId,
    billedText,
    subscriptionPromoText,
    isAnnualOnly,
    crossOutText
  } = props

  return (
    <PlanContainer
      onClick={isAnnualOnly ? undefined : onClick}
      selected={selected}
      $theme={theme}
    >
      <PlanLeftSide hasRadio={!isAnnualOnly}>
        <PlanNameAndBannerContainer>
          <PlanNameContainer>
            <PlanName $theme={theme}>{planName}</PlanName>
          </PlanNameContainer>

          {badgeText && (
            <Badge key={badgeText || `badge-text-${planName}`} $theme={theme}>
              <BadgeText>{badgeText}</BadgeText>
            </Badge>
          )}
        </PlanNameAndBannerContainer>

        <HR $theme={theme}/>

        <PriceText
          $theme={ theme }
          styles={
            subscriptionPromoText && crossOutText
              ? `text-decoration: line-through;`
              : ``
          }
        >
          {text}
        </PriceText>

        {subscriptionPromoText && (
          <PromoPrice $theme={theme} >{subscriptionPromoText}</PromoPrice>
        )}

        <BillingText $theme={theme} >{billedText}</BillingText>
      </PlanLeftSide>

      {!isAnnualOnly && (
        <RadioContainer>
          <Radio
            value={planName}
            name={name}
            isActive={selected}
            groupName={'choosePlanGroup'}
            formId={formId}
          />
        </RadioContainer>
      )}
    </PlanContainer>
  )
}
const PriceText = styled(P16)<any>`
  white-space: pre;
  color: ${(props: any): string => props.$theme === 'pink' ? colors.midnightBlue : colors.white70};
  font-size: 14px;
  line-height: 21px;
`
const PromoPrice = styled(P16)<any>`
  white-space: pre;
  margin-top: 8px;
  font-weight: bold;
  color: ${colors.tintOrange};
  font-size: 14px;
  line-height: 21px;
`

const PlanName = styled.p<any>`
  ${GoodSansB1};
  text-transform: uppercase;
  color: ${(props: any): string => props.$theme === 'pink' ? colors.midnightBlue : colors.white};
`

const BadgeText = styled(P)<any>`
  font-family: 'Good Sans Bold';
  font-size: 12px;
  line-height: 18px;
`
const RadioContainer = styled.div<any>`
  padding: 38px 0 0 0;
`
const PlanContainer = styled.div<any>`
  width: 100%;
  border: 1px solid
    ${(props: any): string => {
      const color = props.$theme === 'pink' ? colors.white : colors.accentBlue
      return props.selected ? color : colors.white
  }};
  box-sizing: border-box;
  backdrop-filter: blur(44px);
  border-radius: 10px;
  background: ${(props: any): string => {
    const color = props.$theme === 'pink' ? colors.white : colors.accentBlue
    return props.selected ? color : 'none'
}};
  cursor: ${(props: { isAnnualOnly: boolean }): string =>
    props.isAnnualOnly ? 'pointer' : 'unset'};
  padding: 12px 17px 15px 15px;
  display: flex;
  margin-bottom: 20px;
  transition: all 0.3s;

  ${(props: any): any => config(props).media.md`
    margin-bottom: 20px;
    padding: 25px 23px 25px 30px;
  `}
    :focus-within {
    box-shadow: 0 0 0 1.25pt ${colors.white};
    outline: none;
    transition: box-shadow 0.25s;
  }
`
const PlanNameAndBannerContainer = styled.div<any>`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
`
const Badge = styled.div<any>`
  background-color: ${(_props: any): string => colors.orange};
  border-radius: 26px;
  margin: -3px 0 0;
  padding: 6px 9px;
  display: flex;
  align-items: center;
`
const PlanNameContainer = styled.div<any>`
  display: flex;
  justify-content: space-between;
`
const HR = styled.hr<any>`
  height: 2px;
  background-color: ${(props: any): string => props.$theme === 'pink' ? colors.pink : colors.white};
  border: none;
  width: 100%;
  margin: 10px 0;

  ${(props: any): any => config(props).media.md`
    margin: 8px 0;
  `}
`
const PlanLeftSide = styled.div<any>`
  margin: 0;
  margin-right: ${(props: { hasRadio: boolean }): string =>
    props.hasRadio ? '14px' : '0'};
  width: 100%;
`
const BillingText = styled(P)<any>`
  margin-top: 16px;
  font-size: 12px;
  line-height: 17px;
  color: ${(props: any): string => props.$theme === 'pink' ? colors.midnightBlue : colors.white70};
`
